import { EarningsItem } from '@api/api/earnings';
import { Entity } from '@api/api/entity-schema';
import { useEarningsFilter } from '@terminal/react-query-hooks/earnings/useEarningsFilter';
import {
  useEarningsInsightRecaps,
  useEarningsInsightRecapsV4,
} from '@terminal/react-query-hooks/insights/useGetEarningsInsightRecap';
import { useCompanyCompetitors } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { httpCode } from '@terminal/utils/api-fetch/httpCode';
import { getCalendarQuarterInfo } from '@terminal/views/theme-overview/tab-content/earnings/utils/utils';
import { useReportedCompaniesGuidance } from '@terminal/views/theme-overview/tab-content/theme-earnings/reported-companies/useReportedCompaniesGuidance';
import { useThemeEarningsInsights } from '@terminal/views/theme-overview/tab-content/theme-earnings/useThemeEarningsInsights';
import { useThemeEarningsThemes } from '@terminal/views/theme-overview/tab-content/theme-earnings/useThemeEarningsThemes';
import { getReportingPeriodLabel } from '@terminal/widgets/earnings/components/reporting-dates/utils';
import { handlePriceData } from '@terminal/widgets/earnings/utils/tableUtils';

import {
  CompetitorEarningsSnapshot,
  formatReportingDate,
  pickClosestEarnings,
  TOP_COMPETITORS_LIMIT,
  TOP_COMPETITORS_ONTOLOGY_VERSION,
} from './utils';

const insightTypes = ['recap', 'preview'] as const;

export interface CompetitorRow {
  entity: Entity;
  rank?: number;
  earnings: CompetitorEarningsSnapshot;
  leadingTheme?: string;
  insightSummary?: string;
  earningsId?: string;
  hasEarningsInsight: boolean;
}

export interface UseTopCompetitorsDataProps {
  entityTag: string;
  activeReportingPeriod: { reporting_date: string };
}

export const useTopCompetitorsData = ({
  entityTag,
  activeReportingPeriod,
}: UseTopCompetitorsDataProps) => {
  const isEarningsInsightsV4Enabled = useFlags(state =>
    state.getFeatureFlagState('REASON-2467-earnings-insights-v4')
  );
  const {
    competitorsData,
    competitorsWithThemesData,
    isFetching: isCompetitorsFetching,
    error: competitorsError,
  } = useCompanyCompetitors({
    entityTag,
    limit: TOP_COMPETITORS_LIMIT,
    version: TOP_COMPETITORS_ONTOLOGY_VERSION,
  });

  const competitorTags = competitorsData.map(item => item.entity.tag);
  // Peers do not share fiscal labels, so the filter window is the calendar
  // quarter of the selected company's reporting date.
  const { startDate, endDate } = getCalendarQuarterInfo(
    new Date(activeReportingPeriod.reporting_date)
  );

  const {
    data: earningsPages,
    isLoading: isEarningsLoading,
    error: earningsError,
  } = useEarningsFilter({
    body: {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      entities: competitorTags,
    },
    shouldFetchEntities: false,
  });

  const earningsByTag = new Map<string, EarningsItem>();
  for (const tag of competitorTags) {
    const closest = pickClosestEarnings(
      (earningsPages?.pages.flat() ?? []).filter(
        earning => earning.entity_tag === tag
      ),
      activeReportingPeriod.reporting_date
    );

    if (closest) {
      earningsByTag.set(tag, closest);
    }
  }

  const earningsRows = [...earningsByTag.values()];
  const guidanceByEarningsId = useReportedCompaniesGuidance(earningsRows);
  const {
    earningsInsightByEarningsId,
    isKgFilterLoading,
    isEarningsInsightsFetching,
  } = useThemeEarningsInsights({
    earningsIds: earningsRows.map(earning => earning.id),
    entityTags: competitorTags,
    insightTypes: [...insightTypes],
  });
  const { themesByEarningsId, isFetching: isThemesFetching } =
    useThemeEarningsThemes(
      earningsInsightByEarningsId,
      'recap',
      !isKgFilterLoading
    );

  const recapIds = [...earningsInsightByEarningsId.values()].flatMap(
    insights => {
      const recap = insights.recap;

      if (!recap) {
        return [];
      }

      if (recap.source === 'earnings-insight') {
        return [recap.id];
      }

      return recap.fallbackEarningsInsightId
        ? [recap.fallbackEarningsInsightId]
        : [];
    }
  );
  const legacyRecaps = useEarningsInsightRecaps(
    isEarningsInsightsV4Enabled ? [] : recapIds
  );
  const v4Recaps = useEarningsInsightRecapsV4(
    isEarningsInsightsV4Enabled ? recapIds : []
  );
  const recaps = isEarningsInsightsV4Enabled
    ? v4Recaps.data
    : legacyRecaps.data;
  const isRecapsFetching = isEarningsInsightsV4Enabled
    ? v4Recaps.isFetching
    : legacyRecaps.isFetching;

  const rankByTag = new Map(
    competitorsWithThemesData.map(competitor => [
      competitor.company,
      competitor.relationship.rank,
    ])
  );
  const keyMetricsByEarningsId = new Map(
    recaps.map(recap => [recap.earnings_id, recap.key_metrics])
  );

  const rows: CompetitorRow[] = competitorsData
    .map(({ entity }) => {
      const earning = earningsByTag.get(entity.tag);
      const insightSummary = earning
        ? keyMetricsByEarningsId.get(earning.id)
        : undefined;
      const hasEarningsInsight = earning
        ? !!earningsInsightByEarningsId.get(earning.id)?.[
            earning.reported ? 'recap' : 'preview'
          ]
        : false;

      return {
        entity,
        rank: rankByTag.get(entity.tag),
        earningsId: earning?.id,
        hasEarningsInsight,
        leadingTheme: earning
          ? themesByEarningsId.get(earning.id)?.[0]?.label
          : undefined,
        insightSummary: insightSummary || undefined,
        earnings: earning
          ? {
              reportingDate: formatReportingDate(earning.reporting_date),
              reportingPeriod: getReportingPeriodLabel(earning),
              hasReported: earning.reported,
              guidance: guidanceByEarningsId.get(earning.id),
              epsSurprisePct: earning.eps.surprise_percentage,
              revenueSurprisePct: earning.revenue.surprise_percentage,
              tPlus1Pct: handlePriceData(earning.price, 'd1'),
              tPlus2Pct: handlePriceData(earning.price, 'd2'),
            }
          : {},
      };
    })
    .sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity));

  const error =
    earningsError ??
    (competitorsError && competitorsError.status !== httpCode.notFound
      ? competitorsError
      : undefined);

  const isLoading =
    isCompetitorsFetching ||
    isEarningsLoading ||
    isEarningsInsightsFetching ||
    isThemesFetching ||
    isRecapsFetching;

  return { rows, isLoading, error };
};
